import { createContext, useContext, useState } from 'react';
import { isEqual } from 'lodash';

import {
  getConversationSelectors,
  isChatCell,
  isExplorationMessage,
  isUserMessage,
  isConversationCell,
  isCellWithPipeline,
} from '@/core/cell';
import { buildExplorationFromCells } from '@/core/exploration';
import { getCellsFromMessages } from '@/core/conversation';
import { isPipelineWithParent, isPipelineChildOf } from '@/core/pipeline';
import {
  useAddMessageMutation,
  useCancelConversationJobMutation,
  useDiscardMessageMutation,
  useRerunConversationJobMutation,
  useDeleteMessageMutation,
  useUpdateMessageMutation,
  useStartConversationMutation,
} from '@/graphql';
import { useSelectedAccount } from '@/lib/accounts/context';
import { generateUUID, notNil } from '@/lib/utils';

import {
  Cell,
  ChatExploration,
  ChatMessage,
  ChatMode,
  ChatUserMessage,
  Conversation,
  ConversationJob,
  Exploration,
  ExplorationParameters,
} from '../types';
import { exportChatMessage, exportExploration } from '../output';
import {
  getVariableDefinitions,
  getQueryVariablesFromParameters,
  ensureValidConversation,
} from '../utils';
import { convertConversationType } from '../input';
import { extractCellFromExploration } from '../exploration/utils';
import { useMetadataContext } from '../metadata-context';

const TempJobPrefix = 'tmp-';

export interface ConversationRepositoryContextValue {
  conversations: Conversation[];
  setConversation: (conversation: Conversation) => void;
  getConversation: (conversationId: string) => Conversation | undefined;
  removeConversation: (conversationId: string) => void;
  getConversationAsExploration: (conversationId: string) => Exploration | undefined;
  setConversationAsExploration: (conversationId: string, exploration: Exploration) => void;
  getExplorationCell: (cellId: string) => Cell | undefined;
  getSelectedConversation: (cellId: string) => Conversation | undefined;
  branchConversation: (oldConversationId: string, newConversationId: string) => void;
  isPendingCreation: (conversationId: string) => boolean;
  startConversation: (
    conversationId: string,
    query: string,
    sources: string[],
    mode: ChatMode,
    conversationSubjectId: string | null,
    correlationId?: string,
  ) => Promise<{ conversation: Conversation | null; error: Error | undefined }>;
  addMessage: (
    conversationId: string,
    message: ChatMessage | ChatExploration,
    correlationId?: string,
  ) => Promise<string>;
  addUserMessage: (
    conversationId: string,
    content: string,
    sources: string[],
    mode: ChatMode,
    correlationId?: string,
  ) => Promise<Exploration | null>;
  updateUserMessage: (
    conversationId: string,
    messageId: string,
    message: ChatUserMessage,
    correlationId?: string,
  ) => Promise<Exploration | null>;
  cancelConversationJob: (conversationId: string, jobId: string) => Promise<void>;
  rerunConversationJob: (conversationId: string, jobId: string) => Promise<void>;
  discardMessage: (conversationId: string, messageId: string) => Promise<void>;
  extractExplorationCells: (conversationId: string) => Cell[];
  extractExplorationCellsAfterMessage: (conversationId: string, messageId: string) => Cell[];
}

const defaultContextValue = Symbol();

export const ConversationRepositoryContext = createContext<
  ConversationRepositoryContextValue | typeof defaultContextValue
>(defaultContextValue);

export const useConversationRepositoryContext = (): ConversationRepositoryContextValue => {
  const context = useContext(ConversationRepositoryContext);
  if (context === defaultContextValue) {
    throw new Error('useConversationRepositoryContext must be used within a ConversationProvider');
  }
  return context;
};

const LoadingMessages = [
  'Analyzing your question',
  'Searching through data',
  'Thinking of a great solution',
  'Retrieving relevant data',
  'Analyzing the information',
  'Ruminating on the question',
  'Conjuring up a response',
  'Tapping into the collective unconscious',
  'Channeling the wisdom of the ages',
  'Plotting the trajectory of insight',
  'Thinking really hard',
  'Accessing mainframe...',
  'Reticulating splines...',
] as const;

/**
 * Generate a loading message based on the message ID.
 * @param messageId
 * @returns A loading message.
 */
const getLoadingMessage = (messageId: string) => {
  let hash = 0;
  for (let i = 0; i < messageId.length; i++) {
    hash = (hash << 5) - hash + messageId.charCodeAt(i);
    hash |= 0;
  }
  return LoadingMessages[Math.abs(hash) % LoadingMessages.length];
};

const createOptimisticJob = (jobId: string, messageId: string): ConversationJob => {
  const createdAt = new Date().toISOString();
  return {
    jobId,
    progress: [
      {
        jobProgressId: `${jobId}-progress-1`,
        jobId,
        status: 'running',
        statusMessage: getLoadingMessage(messageId),
        createdAt,
      },
    ],
    createdAt,
  };
};

export interface ConversationProviderProps {
  exploration: Exploration;
  parameters: ExplorationParameters;
  children: React.ReactNode;
  onBranch: (oldConversationId: string, newConversationId: string) => Exploration;
}

export const ConversationRepositoryContextProvider = ({
  exploration,
  parameters,
  children,
  onBranch,
}: ConversationProviderProps) => {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [pendingConversations, setPendingConversations] = useState<Set<string>>(new Set());

  const { accountId } = useSelectedAccount();
  const { models, metrics } = useMetadataContext();
  const [startConversationMutation] = useStartConversationMutation();
  const [addMessageMutation] = useAddMessageMutation();
  const [editMessageMutation] = useUpdateMessageMutation();
  const [discardMessageMutation] = useDiscardMessageMutation();
  const [cancelConversationJobMutation] = useCancelConversationJobMutation();
  const [rerunConversationJobMutation] = useRerunConversationJobMutation();
  const [deleteMessageMutation] = useDeleteMessageMutation();

  const getVariables = () =>
    getQueryVariablesFromParameters(getVariableDefinitions(exploration), parameters).sort((a, b) =>
      a.key.localeCompare(b.key),
    );

  const setConversation = (conversation: Conversation) => {
    setConversations((prev) => [
      ...prev.filter((c) => c.conversationId !== conversation.conversationId),
      ensureValidConversation(conversation, {
        exploration,
        models,
        metrics,
        parameters,
      }),
    ]);
  };

  const getConversation = (conversationId: string) => {
    return conversations.find((c) => c.conversationId === conversationId);
  };

  const removeConversation = (conversationId: string) => {
    setConversations((prev) => prev.filter((c) => c.conversationId !== conversationId));
    setPendingConversations((prev) => {
      const next = new Set(prev);
      next.delete(conversationId);
      return next;
    });
  };

  const isPendingCreation = (conversationId: string) => {
    return pendingConversations.has(conversationId);
  };

  const branchConversation = (
    oldConversationId: string,
    newConversationId: string,
  ): Exploration | null => {
    if (oldConversationId === newConversationId) {
      return null;
    }

    setConversations((prev) =>
      prev.map((conversation) =>
        conversation.conversationId === oldConversationId
          ? { ...conversation, conversationId: newConversationId }
          : conversation,
      ),
    );
    return onBranch(oldConversationId, newConversationId);
  };

  const optimisticStartConversation = (
    conversationId: string,
    messageId: string,
    message: ChatUserMessage,
  ) => {
    const tmpJobId = TempJobPrefix + messageId;
    setPendingConversations((prev) => new Set(prev).add(conversationId));
    setConversation({
      conversationId,
      messages: [{ ...message, jobId: tmpJobId }],
      jobs: [createOptimisticJob(tmpJobId, messageId)],
    });
  };

  const startConversation = async (
    conversationId: string,
    query: string,
    sources: string[],
    mode: ChatMode,
    conversationSubjectId: string | null,
    correlationId?: string,
  ) => {
    const messageId = crypto.randomUUID();
    const initialMessage = {
      id: messageId,
      role: 'user' as const,
      message: query,
      type: 'initial_user_prompt' as const,
      sources,
      research: mode === 'research',
    };
    optimisticStartConversation(conversationId, messageId, initialMessage);

    const mutation = await startConversationMutation({
      variables: {
        accountId,
        conversationId,
        exploration: exportExploration(exploration),
        variables: getVariables(),
        correlationId: correlationId ?? crypto.randomUUID(),
        selectors:
          conversationSubjectId !== null
            ? [`view.cells[?(@.id == "${conversationSubjectId}")]`]
            : [],
        message: exportChatMessage(initialMessage),
      },
    });

    if (!mutation.data?.startConversation?.conversation) {
      throw new Error('Failed to start conversation');
    }

    const updatedConversation = convertConversationType(
      mutation.data?.startConversation?.conversation,
    );

    setPendingConversations((prev) => {
      const next = new Set(prev);
      next.delete(conversationId);
      return next;
    });

    if (updatedConversation !== null) {
      setConversation(updatedConversation);
    }

    return {
      conversation: updatedConversation,
      error: mutation.errors?.at(0),
    };
  };

  const optimisticRemoveMessage = (conversationId: string, messageId: string) => {
    const conversation = getConversation(conversationId);
    if (conversation === undefined) {
      return;
    }

    const messageIdx = conversation.messages.findIndex((m) => m.id === messageId);
    if (messageIdx === -1) {
      throw new Error(
        `Message with id ${messageId} not found in conversation ${conversation.conversationId}`,
      );
    }
    const message = conversation.messages[messageIdx];
    if (!isExplorationMessage(message)) {
      throw new Error('Cannot remove a message that is not an exploration message');
    }
    // If the next message is not an "answer" message (exploration), delete all non-answer messages above until
    // the previous answer message. This ensures "question" messages are not left without answer messages.
    const nextMessage = conversation.messages[messageIdx + 1];
    const deleteFromIdx =
      nextMessage !== undefined && isExplorationMessage(nextMessage)
        ? messageIdx
        : conversation.messages.slice(0, messageIdx).findLastIndex(isExplorationMessage) + 1;
    const deleteUntilIdx = messageIdx + 1;
    const updatedMessages = [
      ...conversation.messages.slice(0, deleteFromIdx),
      ...conversation.messages.slice(deleteUntilIdx),
    ];
    // If another followup message became the first message, ensure it's of the correct type
    if (updatedMessages.length > 0 && isUserMessage(updatedMessages[0])) {
      updatedMessages[0].type = 'initial_user_prompt';
    }
    setConversation({
      ...conversation,
      messages: updatedMessages,
    });
  };

  const removeMessage = async (conversationId: string, messageId: string) => {
    optimisticRemoveMessage(conversationId, messageId);
    const mutation = await deleteMessageMutation({
      variables: {
        accountId,
        conversationId,
        messageId,
      },
    });
    return mutation.data?.deleteMessage?.conversation.conversationId;
  };

  const optimisticAddMessage = (conversationId: string, message: ChatMessage | ChatExploration) => {
    const conversation = getConversation(conversationId);
    if (conversation === undefined) {
      return;
    }

    const tmpJobId = TempJobPrefix + message.id;
    setConversation({
      ...conversation,
      messages: [...conversation.messages, { ...message, jobId: tmpJobId }],
      jobs: [createOptimisticJob(tmpJobId, message.id)],
    });
  };

  const addMessage = async (
    conversationId: string,
    message: ChatMessage | ChatExploration,
    correlationId?: string,
  ) => {
    optimisticAddMessage(conversationId, message);
    const mutation = await addMessageMutation({
      variables: {
        accountId,
        conversationId,
        exploration: exportExploration(exploration),
        variables: getVariables(),
        correlationId: correlationId ?? crypto.randomUUID(),
        selectors: getConversationSelectors(conversationId, exploration.view.cells),
        message: exportChatMessage(message),
      },
    });
    const newConversationId = mutation.data?.addMessage?.conversation.conversationId;

    if (newConversationId === undefined) {
      throw new Error('Failed to add message');
    }
    return newConversationId;
  };

  const optimisticEditMessage = (
    conversationId: string,
    message: ChatMessage | ChatExploration,
  ) => {
    const conversation = getConversation(conversationId);
    if (conversation === undefined) {
      return;
    }

    if (message.type === 'exploration') {
      setConversation({
        ...conversation,
        messages: conversation.messages.map((m) => (m.id === message.id ? message : m)),
      });
      return;
    }
    if (message.role === 'user') {
      const messageIdx = conversation.messages.findIndex((m) => m.id === message.id);
      const tmpJobId = TempJobPrefix + message.id;
      setConversation({
        ...conversation,
        messages: [...conversation.messages.slice(0, messageIdx), { ...message, jobId: tmpJobId }],
        jobs: [createOptimisticJob(tmpJobId, message.id)],
      });
      return;
    }
  };

  const editMessage = async (
    conversationId: string,
    message: ChatMessage | ChatExploration,
    correlationId?: string,
  ) => {
    optimisticEditMessage(conversationId, message);
    const mutation = await editMessageMutation({
      variables: {
        accountId,
        conversationId,
        exploration: exportExploration(exploration),
        variables: getVariables(),
        correlationId: correlationId ?? crypto.randomUUID(),
        selectors: getConversationSelectors(conversationId, exploration.view.cells),
        message: exportChatMessage(message),
      },
    });
    const newConversationId = mutation.data?.updateMessage?.conversation.conversationId;
    if (newConversationId === undefined) {
      throw new Error('Failed to edit message');
    }
    return newConversationId;
  };

  const applyConversationChanges = async (conversation: Conversation) => {
    const previousConversation = getConversation(conversation.conversationId);
    if (previousConversation === undefined) {
      throw new Error(`Conversation with id ${conversation.conversationId} not found.`);
    }
    const removedMessages = previousConversation.messages.filter(
      (message) => !conversation.messages.some((m) => m.id === message.id),
    );
    const updatedMessages = conversation.messages.filter((message) => {
      const previousMessage = previousConversation.messages.find((m) => m.id === message.id);
      return previousMessage !== undefined && !isEqual(previousMessage, message);
    });
    const newMessages = conversation.messages.filter(
      (message) => !previousConversation.messages.some((m) => m.id === message.id),
    );
    let updatedConversationId: string | undefined;
    for (const message of removedMessages) {
      updatedConversationId = await removeMessage(previousConversation.conversationId, message.id);
    }
    for (const message of updatedMessages) {
      updatedConversationId = await editMessage(previousConversation.conversationId, message);
    }
    for (const message of newMessages) {
      updatedConversationId = await addMessage(previousConversation.conversationId, message);
    }
    if (
      updatedConversationId !== undefined &&
      conversation.conversationId !== updatedConversationId
    ) {
      branchConversation(previousConversation.conversationId, updatedConversationId);
    }
  };

  const getConversationAsExploration = (conversationId: string) => {
    const conversation = getConversation(conversationId);
    if (conversation === undefined) {
      return undefined;
    }
    let messages: ChatMessage[] = [];
    const cells = conversation.messages.flatMap((message, idx) => {
      const nextMessage = conversation.messages[idx + 1];
      if (isExplorationMessage(message)) {
        return message.exploration.view.cells;
      }

      if (nextMessage === undefined || isExplorationMessage(nextMessage)) {
        messages.push(message);
        const chatCell = {
          id: message.id,
          kind: 'chat' as const,
          messages,
          conversationId: conversation.conversationId,
        };
        messages = [];
        return chatCell.messages.length > 0 ? chatCell : [];
      }

      // Group messages until the next exploration message
      messages.push(message);
      return [];
    });

    return buildExplorationFromCells(cells);
  };

  // Supports updating and deleting cells/messages via updating/deleting blocks
  const setConversationAsExploration = async (conversationId: string, exploration: Exploration) => {
    const conversation = getConversation(conversationId);
    if (conversation === undefined) {
      throw new Error(`Conversation with id ${conversationId} not found.`);
    }

    const updatedConversation = {
      ...conversation,
      messages: exploration.view.cells.reduce<(ChatMessage | ChatExploration)[]>((acc, cell) => {
        if (isChatCell(cell)) {
          acc.push(...cell.messages);
        } else {
          const existingMessage = conversation.messages.find(
            (message) =>
              isExplorationMessage(message) &&
              message.exploration.view.cells.some((c) => c.id === cell.id),
          );

          if (existingMessage && isExplorationMessage(existingMessage)) {
            // Update existing exploration message cells
            const updatedExploration = {
              ...existingMessage.exploration,
              view: {
                ...existingMessage.exploration.view,
                cells: existingMessage.exploration.view.cells
                  .map((c) => {
                    const updatedCell = exploration.view.cells.find((ec) => ec.id === c.id);
                    return updatedCell ? updatedCell : null;
                  })
                  .filter(notNil),
              },
            };
            acc.push({
              ...existingMessage,
              exploration: updatedExploration,
            });
          } else {
            // Create new exploration message from the cell
            const newExploration = buildExplorationFromCells([cell]);
            acc.push({
              id: cell.id,
              role: 'assistant',
              type: 'exploration',
              exploration: newExploration,
            });
          }
        }
        return acc;
      }, []),
    };
    await applyConversationChanges(updatedConversation);
    return updatedConversation;
  };

  const addUserMessage = async (
    conversationId: string,
    content: string,
    sources: string[],
    mode: ChatMode,
    correlationId?: string,
  ) => {
    const conversation = getConversation(conversationId);
    if (conversation === undefined) {
      throw new Error('Conversation not found');
    }
    const message =
      conversation.messages.length === 0
        ? {
            id: generateUUID(),
            type: 'initial_user_prompt' as const,
            role: 'user' as const,
            message: content,
            sources,
            research: mode === 'research',
          }
        : conversation.messages.at(-1)?.type === 'clarifying_question'
          ? {
              id: generateUUID(),
              type: 'clarifying_answer' as const,
              role: 'user' as const,
              message: content,
              sources,
            }
          : {
              id: generateUUID(),
              type: 'followup_question' as const,
              role: 'user' as const,
              message: content,
              sources,
              research: mode === 'research',
            };

    const newConversationId = await addMessage(conversationId, message, correlationId);
    return branchConversation(conversationId, newConversationId);
  };

  const updateUserMessage = async (
    conversationId: string,
    messageId: string,
    message: ChatUserMessage,
    correlationId?: string,
  ) => {
    const conversation = getConversation(conversationId);
    if (conversation === undefined) {
      throw new Error('Conversation not found');
    }
    const currentMessage = conversation.messages.find((msg) => msg.id === messageId);
    if (currentMessage === undefined) {
      throw new Error(`Message with id ${messageId} not found in conversation.`);
    }
    if (!isUserMessage(currentMessage)) {
      throw new Error(`Message with id ${messageId} is not a user message.`);
    }
    const sources = 'sources' in message ? message.sources : null;
    const research = 'research' in message ? message.research : null;
    const newConversationId = await editMessage(
      conversationId,
      {
        ...currentMessage,
        message: message.message,
        ...(sources === null ? {} : { sources }),
        ...(research === null ? {} : { research }),
      },
      correlationId,
    );
    return branchConversation(conversationId, newConversationId);
  };

  const cancelConversationJob = async (conversationId: string, jobId: string) => {
    const mutation = await cancelConversationJobMutation({
      variables: {
        accountId,
        conversationId,
        jobId,
      },
    });

    const newConversationId = mutation.data?.cancelConversationJob?.conversation.conversationId;
    if (newConversationId === undefined) {
      throw new Error('Failed to cancel conversation job');
    }
    branchConversation(conversationId, newConversationId);
  };

  const rerunConversationJob = async (conversationId: string, jobId: string) => {
    const mutation = await rerunConversationJobMutation({
      variables: {
        accountId,
        conversationId,
        jobId,
      },
    });
    const newConversationId = mutation.data?.rerunConversationJob?.conversation.conversationId;
    if (newConversationId === undefined) {
      throw new Error('Failed to rerun conversation job');
    }
    branchConversation(conversationId, newConversationId);
  };

  const optimisticDiscardMessage = (conversationId: string, messageId: string) => {
    const conversation = getConversation(conversationId);
    if (conversation === undefined) {
      return;
    }

    const messageIdx = conversation.messages.findIndex((m) => m.id === messageId);
    if (messageIdx === -1) {
      throw new Error(
        `Message with id ${messageId} not found in conversation ${conversation.conversationId}`,
      );
    }
    const userMessageIdx = conversation.messages.findLastIndex(
      (m, idx) => idx <= messageIdx && isUserMessage(m),
    );

    if (userMessageIdx === -1) {
      return conversation;
    }

    setConversation({
      ...conversation,
      messages: conversation.messages.slice(0, userMessageIdx),
    });
  };

  const discardMessage = async (conversationId: string, messageId: string) => {
    optimisticDiscardMessage(conversationId, messageId);
    const mutation = await discardMessageMutation({
      variables: {
        accountId,
        conversationId,
        messageId,
      },
    });
    const newConversationId = mutation.data?.discardMessage?.conversation.conversationId;
    if (newConversationId === undefined) {
      throw new Error('Failed to discard message');
    }
    branchConversation(conversationId, newConversationId);
  };

  const getExplorationCell = (cellId: string) => {
    return getSelectedConversation(cellId)
      ?.messages.filter(isExplorationMessage)
      .flatMap((message) => message.exploration.view.cells)
      .find((cell) => cell.id === cellId);
  };

  const getSelectedConversation = (cellId: string) => {
    const conversationsWithCell = conversations.filter((conversation) => {
      return conversation.messages.some((message) => {
        return (
          isExplorationMessage(message) &&
          message.exploration.view.cells.some((cell) => cell.id === cellId)
        );
      });
    });
    return conversationsWithCell.find((conversation) =>
      exploration.view.cells.some(
        (cell) => isConversationCell(cell) && cell.conversationId === conversation.conversationId,
      ),
    );
  };

  const extractExplorationCells = (conversationId: string): Cell[] => {
    const conversation = getConversation(conversationId);
    if (conversation === undefined) {
      throw new Error(`Conversation with id ${conversationId} not found.`);
    }
    return getCellsFromMessages(conversation.messages);
  };

  /**
   * Extract exploration cells from exploration messages a specific message. Inludes the message itself.
   * @param conversationId The ID of the conversation.
   * @param messageId The ID of the message.
   * @returns An array of exploration cells.
   */
  const extractExplorationCellsAfterMessage = (
    conversationId: string,
    messageId: string,
  ): Cell[] => {
    const conversation = getConversation(conversationId);
    if (conversation === undefined) {
      throw new Error(`Conversation with id ${conversationId} not found.`);
    }
    const messageIdx = conversation.messages.findIndex((msg) => msg.id === messageId);
    if (messageIdx === -1) {
      throw new Error(`Message with id ${messageId} not found in conversation.`);
    }

    const conversationCells = extractExplorationCells(conversationId);

    // Extract referenced pipelines
    const allCells = conversationCells.concat(exploration.view.cells);
    const cellsToKeep = getCellsFromMessages(conversation.messages.slice(messageIdx));
    const cellsToDiscard = getCellsFromMessages(conversation.messages.slice(0, messageIdx));
    const interimExploration = buildExplorationFromCells(allCells);

    return cellsToKeep
      .map((cell) => {
        const hasParent = isCellWithPipeline(cell) && isPipelineWithParent(cell.pipeline);

        if (
          hasParent &&
          cellsToDiscard.some(
            (c) => isCellWithPipeline(c) && isPipelineChildOf(cell.pipeline, c.pipeline),
          )
        ) {
          // Extract only if referenced pipeline belongs to one of the cells to be discarded within this conversation
          const { cell: extractedCell, dependencies } = extractCellFromExploration(
            cell.id,
            interimExploration,
          );
          return [extractedCell, ...dependencies];
        }

        return cell;
      })
      .flat();
  };

  return (
    <ConversationRepositoryContext.Provider
      value={{
        conversations,
        setConversation,
        getConversation,
        removeConversation,
        getConversationAsExploration,
        setConversationAsExploration,
        getExplorationCell,
        getSelectedConversation,
        branchConversation,
        isPendingCreation,
        startConversation,
        addMessage,
        addUserMessage,
        updateUserMessage,
        cancelConversationJob,
        rerunConversationJob,
        discardMessage,
        extractExplorationCells,
        extractExplorationCellsAfterMessage,
      }}>
      {children}
    </ConversationRepositoryContext.Provider>
  );
};
